/*
 * Voice input recorder: one recording of the microphone, in a format the server's speech service
 * reads (Opus in WebM or OGG; MP4 on Safari). The microphone is asked for only when a recording
 * starts, and it is always released when the recording ends — stopped, cancelled, failed, or
 * abandoned because the composer went away. Nothing of the audio is kept here once it is handed
 * over.
 */

/** In the order they are tried; when none is supported the browser picks its own format. */
export const VOICE_MIME_TYPES = [
  'audio/webm;codecs=opus',
  'audio/ogg;codecs=opus',
  'audio/mp4',
  'audio/webm',
]

/** The largest recording the server takes (10 MB). */
export const VOICE_MAX_BYTES = 10 * 1024 * 1024

const FILE_EXTENSIONS: Record<string, string> = {
  'audio/webm': 'webm',
  'audio/ogg': 'ogg',
  'audio/mp4': 'mp4',
  'audio/mpeg': 'mp3',
  'audio/wav': 'wav',
  'audio/x-wav': 'wav',
  'audio/aac': 'aac',
}

/** The browser can record the microphone: without it the composer shows no microphone. */
export const isVoiceRecordingSupported = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.MediaRecorder === 'function' &&
  typeof navigator !== 'undefined' &&
  typeof navigator.mediaDevices?.getUserMedia === 'function'

/** The first format of VOICE_MIME_TYPES the browser records; undefined lets it choose. */
export const pickVoiceMimeType = (): string | undefined => {
  const Recorder = window.MediaRecorder
  if (typeof Recorder?.isTypeSupported !== 'function') {
    return undefined
  }
  return VOICE_MIME_TYPES.find(type => {
    try {
      return Recorder.isTypeSupported(type)
    } catch {
      return false
    }
  })
}

/** "audio/webm;codecs=opus" → "audio/webm". */
export const baseMimeType = (type: string) =>
  type.split(';')[0].trim().toLowerCase()

/** The name the recording is sent under; its extension follows the format. */
export const voiceFileName = (mimeType: string) =>
  `voice.${FILE_EXTENSIONS[baseMimeType(mimeType)] ?? 'webm'}`

export interface VoiceRecording {
  audio: Blob
  fileName: string
}

const releaseStream = (stream: MediaStream | null) => {
  stream?.getTracks().forEach(track => {
    try {
      track.stop()
    } catch {
      // a track that cannot be stopped has already ended
    }
  })
}

export class VoiceRecorder {
  private stream: MediaStream | null = null
  private recorder: MediaRecorder | null = null
  private chunks: Blob[] = []
  /** Set once the recording is over: a microphone granted after that is released at once. */
  private ended = false
  /** The caller of stop(), waiting for the recorder to hand over the last of the audio. */
  private settle: ((_recording: VoiceRecording | null) => void) | null = null

  /** Called when the recording ended by itself (the microphone was unplugged, an error). */
  private readonly onFailure?: () => void

  constructor(onFailure?: () => void) {
    this.onFailure = onFailure
  }

  get isRecording() {
    return !this.ended && this.recorder !== null
  }

  /**
   * Asks for the microphone and starts recording. False when the recording was cancelled while
   * the browser was asking the user. Rejects with the browser's error (a DOMException named
   * NotAllowedError, NotFoundError, NotReadableError…), with the microphone released.
   */
  async start(): Promise<boolean> {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    if (this.ended) {
      releaseStream(stream)
      return false
    }
    this.stream = stream
    try {
      const mimeType = pickVoiceMimeType()
      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream)
      recorder.ondataavailable = event => {
        if (event.data && event.data.size > 0) {
          this.chunks.push(event.data)
        }
      }
      recorder.onstop = () => this.onStopped()
      recorder.onerror = () => this.fail()
      this.recorder = recorder
      recorder.start()
    } catch (error) {
      this.ended = true
      this.release()
      throw error
    }
    return true
  }

  /**
   * Ends the recording and gives its audio; null when nothing was recorded (or it was cancelled
   * meanwhile). The microphone is released either way.
   */
  stop(): Promise<VoiceRecording | null> {
    const recorder = this.recorder
    if (this.ended || !recorder) {
      this.cancel()
      return Promise.resolve(null)
    }
    this.ended = true
    return new Promise(resolve => {
      this.settle = resolve
      if (recorder.state === 'inactive') {
        this.finish()
        return
      }
      try {
        recorder.stop()
      } catch {
        this.finish()
      }
    })
  }

  /** Ends the recording and throws its audio away; the microphone is released. */
  cancel() {
    this.ended = true
    const recorder = this.recorder
    if (recorder) {
      recorder.ondataavailable = null
      recorder.onstop = null
      recorder.onerror = null
      if (recorder.state !== 'inactive') {
        try {
          recorder.stop()
        } catch {
          // already stopped
        }
      }
    }
    this.chunks = []
    this.release()
    const settle = this.settle
    this.settle = null
    settle?.(null)
  }

  private onStopped() {
    if (this.settle) {
      this.finish()
      return
    }
    // Stopped without stop() being called: the recorder ended by itself.
    this.fail()
  }

  private fail() {
    if (this.ended && !this.settle) {
      return
    }
    const waiting = this.settle !== null
    this.cancel()
    if (!waiting) {
      this.onFailure?.()
    }
  }

  private finish() {
    const settle = this.settle
    this.settle = null
    const type = baseMimeType(
      this.recorder?.mimeType || this.chunks[0]?.type || 'audio/webm'
    )
    const audio = new Blob(this.chunks, { type })
    this.chunks = []
    this.release()
    settle?.(audio.size > 0 ? { audio, fileName: voiceFileName(type) } : null)
  }

  private release() {
    releaseStream(this.stream)
    this.stream = null
  }
}
